import {
  type ReactNode,
  useMemo,
  useState,
} from 'react'
import {
  Alert,
  Box,
  Button,
  Center,
  Loader,
  Tabs,
} from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { FlaskConical } from 'lucide-react'
import {
  usePromoteToStaged,
  useStagedDevicesQuery,
  useTestDevicesQuery,
} from '@data/device/device'
import {
  TestBenchTable,
  type TestBenchVariant,
} from './TestBenchTable'
import { TestDeviceModal } from './TestDeviceModal'

interface DeviceListStateProps {
  label: string
  isPending: boolean
  isError: boolean
  onRetry: () => void
  children: ReactNode
}

// Shared by both tabs: the loading and failure states wrap each table identically.
const DeviceListState = ({
  label,
  isPending,
  isError,
  onRetry,
  children,
}: DeviceListStateProps) => {
  if (isPending) {
    return (
      <Center mt='xl'>
        <Loader
          aria-label={`Loading ${label}`}
          role='status'
        />
      </Center>
    )
  }

  if (isError) {
    return (
      <Alert
        mt='md'
        variant='light'
        color='red'
        icon={<IconAlertCircle size={16} />}
        title={`Could not load ${label}`}
      >
        <Button
          size='xs'
          variant='light'
          color='red'
          onClick={onRetry}
        >
          Retry
        </Button>
      </Alert>
    )
  }

  return <>{children}</>
}

function Dashboard() {
  const {
    data: testDevices = [],
    isPending: testDevicesPending,
    isError: testDevicesError,
    refetch: refetchTestDevices,
  } = useTestDevicesQuery()
  const {
    data: stagedDevices = [],
    isPending: stagedDevicesPending,
    isError: stagedDevicesError,
    refetch: refetchStagedDevices,
  } = useStagedDevicesQuery()
  // `mutate` rather than `mutateAsync`: the hook reports failures through its own toast, and
  // `mutate` is stable across renders, so the table's columns are not rebuilt on every render.
  const { mutate: promoteToStaged } = usePromoteToStaged()

  const [activeTab, setActiveTab] = useState<TestBenchVariant>('staged')
  const [selectedTestDeviceId, setSelectedTestDeviceId] = useState<string>()

  // Derived from the list rather than held as a snapshot, as Device Management does, so a
  // refresh shows in the open modal and a device that leaves the list closes it.
  const selectedTestDevice = useMemo(
    () => testDevices.find((device) => device.deviceId === selectedTestDeviceId),
    [selectedTestDeviceId, testDevices],
  )

  return (
    <Box
      h='100%'
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <h1>
        <FlaskConical
          size={34}
          style={{
            marginRight: '1rem',
            marginLeft: '1rem',
          }}
        />
        Smartflow Test Bench
      </h1>
      <Box
        className='container-fluid flex-container'
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {/*
          keepMounted so each tab's table keeps its own filters, sort and page while the other
          tab is showing. Unmounting on switch would reset them every time.

          Mantine 9 implements keepMounted with React 19's <Activity>: a hidden tab keeps its DOM
          and state, but its effects wait until the tab is first shown. So the hidden table's
          mount-time preference writes (its default column order and sizing) only reach
          localStorage once that tab is opened. Nothing is lost, because every user change saves
          through its handler immediately, so a missing test-tab key before first view is
          expected. Do not switch to keepMountedMode='display-none' to make it appear sooner.
        */}
        <Tabs
          value={activeTab}
          onChange={(value) => setActiveTab(value === 'test' ? 'test' : 'staged')}
          keepMounted
        >
          <Tabs.List>
            <Tabs.Tab value='staged'>
              Staged Devices
            </Tabs.Tab>
            <Tabs.Tab value='test'>
              Test Devices
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value='staged'>
            <DeviceListState
              label='staged devices'
              isPending={stagedDevicesPending}
              isError={stagedDevicesError}
              onRetry={() => void refetchStagedDevices()}
            >
              <TestBenchTable
                variant='staged'
                devices={stagedDevices}
              />
            </DeviceListState>
          </Tabs.Panel>

          <Tabs.Panel value='test'>
            <DeviceListState
              label='test devices'
              isPending={testDevicesPending}
              isError={testDevicesError}
              onRetry={() => void refetchTestDevices()}
            >
              <TestBenchTable
                variant='test'
                devices={testDevices}
                onStageDevice={promoteToStaged}
                onDeviceClick={(device) => setSelectedTestDeviceId(device.deviceId)}
              />
            </DeviceListState>
          </Tabs.Panel>
        </Tabs>

        {selectedTestDevice ? (
          <TestDeviceModal
            opened
            device={selectedTestDevice}
            onClose={() => setSelectedTestDeviceId(undefined)}
          />
        ) : null}
      </Box>
    </Box>
  )
}

export default Dashboard
